import {
  ActionName,
  CategoryName,
  DomainName,
  ExecutedActionP,
  ICategoryEntryParsed,
  ICategoryParsed,
  IRefCategoryFieldParsed,
  IViewFieldParsed,
  IViewFieldWithDomainMD,
  IViewSchema,
  tg,
} from '@infotech-soe/metaschema';
import { fromPairs, isEmpty } from 'lodash';

import ObjectStorage from '../../persistence/ObjectStorage';
import IApiProvider from '../../providers/abstract/IApiProvider';
import ISchemaParser, { ParseCategoryResult } from '../parser/ISchemaParser';
import { resolve } from '../parser/utils';

import IStoredSchemaNameManager from './IStoredSchemaNameManager';
import {
  StoredCategoryActions,
  StoredCategoryList,
  StoredDomainList,
  StoredFormFields,
  StoredSchemaSource,
  StoredViewFields,
} from './StoredSchemaSource';

const isDomainBasedViewField = (
  v: IViewFieldParsed
): v is IViewFieldWithDomainMD => tg.hasDomainProp(v);

const isRefBasedCategoryField = (
  x: ICategoryEntryParsed
): x is IRefCategoryFieldParsed => tg.hasProperty(x, 'category');

class ResolutionError extends Error {
  constructor(baseEntity: string) {
    super(`unknown type of ${baseEntity}, cannot resolve domain`);
  }
}

export class FieldDomainResolver {
  constructor(
    private readonly parser: ISchemaParser,
    private readonly api: IApiProvider,
    private readonly storage: ObjectStorage<
      StoredSchemaSource | StoredDomainList | StoredCategoryList
    >,
    private readonly nameManager: IStoredSchemaNameManager
  ) {}

  private async parseRelationalField(
    currentCategory: string,
    path: string
  ): Promise<DomainName> {
    const field = await this.api.schemas.getField(currentCategory, path);

    return field.domain;
  }

  private async parseCategoryField(
    field: ICategoryEntryParsed
  ): Promise<DomainName> {
    if (tg.hasDomainProp(field)) {
      return field.domain;
    } else if (isRefBasedCategoryField(field)) {
      if (field.referenceField) {
        return this.parseRelationalField(field.category, field.referenceField);
      }
      return (await this.api.schemas.getPrimaryField(field.category)).domain;
    }

    throw new ResolutionError(`category field (${JSON.stringify(field)})`);
  }

  private async getCategory(name: CategoryName): Promise<ParseCategoryResult> {
    let categorySource = (
      (await this.storage.get(
        this.nameManager.buildCategoryName(name)
      )) as StoredSchemaSource
    )?.source;

    if (!categorySource) {
      categorySource = (await this.api.schemas.getCategorySchema(name))[0]
        .source;
    }

    return this.parser.parseCategory(name, categorySource);
  }

  async resolveActionInArgs(
    { Args }: ExecutedActionP,
    category: ICategoryParsed,
    categoryName: string
  ): Promise<StoredCategoryActions> {
    if (!Args || isEmpty(Args)) return {};

    return fromPairs(
      await Promise.all(
        Object.entries(Args).map(async ([name, def]) => {
          if (tg.hasDomainProp(def)) {
            return [name, def.domain];
          }

          if (tg.hasCategoryFieldPath(def)) {
            if (def.field.includes('.')) {
              return [
                name,
                await this.parseRelationalField(categoryName, def.field),
              ];
            }
            return [name, await this.parseCategoryField(category[def.field])];
          }

          throw new ResolutionError(`action arg (${name})`);
        })
      )
    );
  }

  async resolveView({ name, source }: IViewSchema): Promise<StoredViewFields> {
    const parsed = this.parser.parseView(name, source);
    const categoryName = parsed.Base;
    const parsedCategory = await this.getCategory(categoryName);

    const fields = [];
    for (const [fieldName, def] of Object.entries(parsed.Fields)) {
      if (isDomainBasedViewField(def)) {
        fields.push([fieldName, def.domain]);
      } else {
        const val = def.field;
        const resolved = resolve(val, categoryName);

        if (!resolved.isSame || resolved.isRef) {
          fields.push([
            fieldName,
            await this.parseRelationalField(resolved.category, resolved.path),
          ]);
        } else {
          const fieldInCurrentCategory = parsedCategory.category[val];

          fields.push([
            fieldName,
            await this.parseCategoryField(fieldInCurrentCategory),
          ]);
        }
      }
    }

    return fromPairs(fields);
  }

  async resolveFormFields(
    source: string,
    name: ActionName,
    categoryName: CategoryName
  ): Promise<StoredFormFields> {
    const parsed = this.parser.parseForm(categoryName, name, source);
    const parsedCategory = await this.getCategory(categoryName);

    const result: any = {};
    for (const [fieldName, def] of Object.entries(parsed.Fields)) {
      if (tg.hasDomainProp(def)) {
        result[fieldName] = def.domain;
        continue;
      }

      const resolvedField = resolve(def.field, categoryName);

      if (resolvedField.isSame && !resolvedField.isRef) {
        result[fieldName] = await this.parseCategoryField(
          parsedCategory.category[resolvedField.path]
        );
        continue;
      }

      const field = await this.api.schemas.getField(
        resolvedField.category,
        resolvedField.path
      );

      result[fieldName] = field.domain;
    }

    return result;
  }
}
